'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@achra/ui/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { InfoIcon } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MobileDialogContent } from '@/modules/shared/components/mobile-dialog-content';
interface TooltipInfoIconProps {
  tooltipContent: React.ReactNode;
  className?: string;
}
const TOOLTIP_WIDTH = 320;
function TooltipInfoIcon(t0) {
  const $ = _c(22);
  const {
    tooltipContent,
    className
  } = t0;
  const [alignment, setAlignment] = useState("start");
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const newAlignment = rect.right + TOOLTIP_WIDTH > viewportWidth ? "end" : "start";
        setAlignment(newAlignment);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const calculateAlignment = t1;
  let t2;
  let t3;
  if ($[1] !== isOpen) {
    t2 = () => {
      if (!isOpen) {
        return;
      }
      window.addEventListener("resize", calculateAlignment);
      return () => {
        window.removeEventListener("resize", calculateAlignment);
      };
    };
    t3 = [calculateAlignment, isOpen];
    $[1] = isOpen;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = open => {
      setIsOpen(open);
      if (open) {
        calculateAlignment();
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleOpenChange = t4;
  let t5;
  if ($[5] !== className) {
    t5 = cn("flex cursor-pointer items-center", className);
    $[5] = className;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DialogPrimitive.Trigger asChild={true}><button type="button" aria-label="View more information"><InfoIcon className="text-muted-foreground h-4 w-4" /></button></DialogPrimitive.Trigger>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <VisuallyHidden><DialogPrimitive.Title>Tooltip</DialogPrimitive.Title></VisuallyHidden>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== tooltipContent) {
    t8 = <div className="md:hidden"><DialogPrimitive.Root>{t6}<MobileDialogContent>{t7}<div className="text-muted-foreground text-sm">{tooltipContent}</div></MobileDialogContent></DialogPrimitive.Root></div>;
    $[9] = tooltipContent;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <TooltipPrimitive.Trigger asChild={true}><button ref={triggerRef} type="button" aria-label="View more information" className="flex cursor-pointer items-center"><InfoIcon className="text-muted-foreground h-4 w-4" /></button></TooltipPrimitive.Trigger>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("bg-popover text-popover-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-80 origin-(--radix-tooltip-content-transform-origin) rounded-xl border p-4 shadow-md");
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== tooltipContent) {
    t11 = <div className="text-sm">{tooltipContent}</div>;
    $[13] = tooltipContent;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== alignment || $[16] !== t11) {
    t12 = <div className="hidden md:block"><TooltipPrimitive.Provider delayDuration={200}><TooltipPrimitive.Root onOpenChange={handleOpenChange}>{t9}<TooltipPrimitive.Portal><TooltipPrimitive.Content side="bottom" sideOffset={10} collisionPadding={16} align={alignment} className={t10}>{t11}</TooltipPrimitive.Content></TooltipPrimitive.Portal></TooltipPrimitive.Root></TooltipPrimitive.Provider></div>;
    $[15] = alignment;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t5 || $[20] !== t8) {
    t13 = <div className={t5}>{t8}{t12}</div>;
    $[18] = t12;
    $[19] = t5;
    $[20] = t8;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  return t13;
}
export { TooltipInfoIcon };